import { create } from 'zustand';
import { BaulAppearance, PhotoAsset } from '@/types';

export type MyPhotosFilter = 'todas' | 'sin-compartir';

// State for the user-scoped "Mis fotos" screen — deliberately not keyed by baulId (there is
// none): a single accumulated list, same infinite-scroll shape as
// useBaulesStore.baulPhotos/baulPhotosHasMore. Actions live in
// features/photos/useCases/loading.ts, same ownership rule as every other photo store.
//
// Only one filter's page is ever cached at a time (Slice 3, docs/.backlog issue #62): switching
// filter clears assets/hasMore and starts a fresh paginated load, same as BaulPhotosTabContainer
// re-fetching "Todas" — there's no need to hold both lists in memory for a screen the user
// looks at one filter of at a time.
export interface MyPhotosState {
  filter: MyPhotosFilter;
  // undefined = not fetched yet (first load in flight/pending); [] = fetched, empty.
  assets: PhotoAsset[] | undefined;
  hasMore: boolean;

  reset: () => void;
  setFilter: (filter: MyPhotosFilter) => void;
  setPage: (assets: PhotoAsset[], hasMore: boolean) => void;
  appendPage: (assets: PhotoAsset[], hasMore: boolean) => void;

  // Prepends freshly-ingested assets from a direct Mis fotos upload (Slice 3) onto whichever
  // filter is currently loaded — see uploadToMyPhotos, the only caller, which only passes
  // assets eligible for the active filter (an asset already shared to a baúl is never passed
  // while "Sin compartir" is active).
  prependUploaded: (assets: PhotoAsset[]) => void;

  // Patches one asset's "Aparece en" list after "Añadir a otro baúl" (docs/.backlog issue #62,
  // Slice 2 — Mis fotos wiring) — a no-op if the appearance is already there, mirroring the
  // backend's own idempotency instead of trusting the caller never to double-add. On "Sin
  // compartir" (GitHub issue #80) the asset is dropped from the loaded list instead, since it's
  // no longer unshared — same effect as removeAssets.
  addBaulAppearance: (assetId: string, appearance: BaulAppearance) => void;

  // "Quitar de Mis fotos" (Slice 5, docs/.backlog issue #62) — drops the given assets from the
  // loaded page(s) after the backend soft-deletes their UserPhotoAsset relation, single and
  // batch alike. A no-op for any id not currently loaded (already scrolled past, or the store
  // hasn't fetched yet) — nothing to reconcile against in that case.
  removeAssets: (assetIds: string[]) => void;
}

export const useMyPhotosStore = create<MyPhotosState>((set) => ({
  filter: 'todas',
  assets: undefined,
  hasMore: true,

  reset: () => set({ filter: 'todas', assets: undefined, hasMore: true }),

  setFilter: (filter) => set({ filter, assets: undefined, hasMore: true }),

  setPage: (assets, hasMore) => set({ assets, hasMore }),

  appendPage: (assets, hasMore) => set((state) => ({ assets: [...(state.assets ?? []), ...assets], hasMore })),

  prependUploaded: (assets) => set((state) => (state.assets === undefined ? state : { assets: [...assets, ...state.assets] })),

  addBaulAppearance: (assetId, appearance) => set((state) => {
    const asset = state.assets?.find((a) => a.id === assetId);
    if (!asset || asset.baules.some((b) => b.baulId === appearance.baulId)) return {};

    // On "Sin compartir" the asset is no longer unshared once it gains a baúl appearance, so it
    // must drop out of the loaded list immediately (GitHub issue #80) — same effect as
    // removeAssets for "Quitar de Mis fotos". On "Todas" the asset stays visible, just with the
    // new appearance merged in.
    if (state.filter === 'sin-compartir') {
      return { assets: state.assets?.filter((a) => a.id !== assetId) };
    }

    return {
      assets: state.assets?.map((a) => (a.id === assetId ? { ...a, baules: [...a.baules, appearance] } : a)),
    };
  }),

  removeAssets: (assetIds) => set((state) => (
    state.assets === undefined ? state : { assets: state.assets.filter((asset) => !assetIds.includes(asset.id)) }
  )),
}));
